// Spec is stored/transmitted as a single TEXT column (`spec: string`, unchanged BE contract),
// but edited as discrete tags in the UI. These two pure helpers are the boundary conversion:
// splitSpec turns the joined string into chips, joinSpec turns chips back into the joined
// string. Delimiter is a semicolon (';'), joined with '; ' for readability — see the plan at
// docs/superpowers/plans/2026-07-21-spec-tags-input.md for the locked decision. Legacy specs
// that already contain ';' naturally split into multiple chips, which is desirable: it lets
// the weekly spec-mining review treat them the same as freshly-tagged entries.

// Splits a joined spec string into its constituent parts: split on ';', trim whitespace off
// each part, drop empty parts (including whitespace-only ones), then dedupe case-sensitively
// while preserving first-seen order (so re-typing an existing tag doesn't create a duplicate
// chip).
export const splitSpec = (spec: string | null | undefined): string[] => {
  if (!spec) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of spec.split(';')) {
    const part = raw.trim();
    if (!part) continue;
    if (seen.has(part)) continue;
    seen.add(part);
    result.push(part);
  }
  return result;
};

// Joins spec parts back into the single stored string. Returns '' when empty so existing
// call-site idioms (`spec.trim() || emptyTo`, `if (spec.trim())`) behave identically to when
// spec was a plain text input.
export const joinSpec = (parts: string[]): string => parts.join('; ');
